import { BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { del, get, post, put } from 'aws-amplify/api';
import { SnackbarManager } from 'rhyme-component-library';

type AmplifyBaseQueryArgs = {
	method: 'GET' | 'POST' | 'PUT' | 'DELETE';
	apiName: string;
	path: string;
	options?: Record<string, any>;
	useSnackbar?: boolean;
	successMsg?: string;
	errorMsg?: string;
};

type ApiResponse = {
	body: any;
	headers: Record<string, string>;
	statusCode: number;
};

/**
 * RTK provides a generic base query hat uses fetch. A custom setup is needed to support
 * AWS amplify usage. All successfull responses should have a status of 200 & snackbars
 * render for create, update, delete, & failures.
 */
export const amplifyBaseQuery: BaseQueryFn<AmplifyBaseQueryArgs, unknown, unknown> = async ({
	method,
	apiName,
	path,
	options = {},
	useSnackbar,
	successMsg,
	errorMsg,
}) => {
	try {
		let response;
		switch (method) {
			case 'GET':
				response = (await get({ apiName, path, options }).response) as ApiResponse;
				if (useSnackbar)
					snackbarNotification(response, {
						success: successMsg,
						error: errorMsg,
					});
				break;
			case 'POST':
				response = (await post({ apiName, path, options }).response) as ApiResponse;
				if (useSnackbar)
					snackbarNotification(response, {
						success: successMsg,
						error: errorMsg,
					});
				break;
			case 'PUT':
				response = (await put({ apiName, path, options }).response) as ApiResponse;
				if (useSnackbar)
					snackbarNotification(response, {
						success: successMsg,
						error: errorMsg,
					});
				break;
			case 'DELETE':
				response = (await del({ apiName, path, options }).response) as ApiResponse;
				if (useSnackbar)
					snackbarNotification(response, {
						success: successMsg,
						error: errorMsg,
					});
				break;
			default:
				throw new Error(`Unsupported method: ${method}`);
		}

		const data = await response.body.json();
		return { data };
	} catch (error) {
		return { error };
	}
};

const snackbarNotification = (
	response: ApiResponse,
	msg: { success: string | undefined; error: string | undefined }
): void => {
	if (response.statusCode === 200) {
		if (msg.success) {
			SnackbarManager.show({
				message: msg.success,
				type: 'success',
			});
		}
	} else {
		if (msg.error) {
			SnackbarManager.show({
				message: msg.error,
				type: 'error',
			});
		}
	}
};
